import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NgpAvatar, NgpAvatarFallback, NgpAvatarImage } from 'ng-primitives/avatar';

import { AppProfileType } from '../../application/models/profile.model';
import { McIcon } from 'makeclub-ui';
import { PersonalStatus } from '../../api/api-clients';
import { environment } from '../../environments/environment';

export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

// Size → Tailwind class for the host element (overall dimensions)
const SIZE_CLASSES: Record<AvatarSize, string> = {
  sm: 'size-8', // 32px
  md: 'size-12', // 48px
  lg: 'size-24', // 96px
  xl: 'size-[312px]', // 312px
};

// Size → text size class for the fallback initials
const INITIALS_TEXT_CLASSES: Record<AvatarSize, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-4xl',
  xl: 'text-6xl',
};

// Size → verified badge dimensions and icon size
const BADGE_SIZE_CLASSES: Record<AvatarSize, string> = {
  sm: 'size-6 text-3xl',
  md: 'size-6 text-3xl',
  lg: 'size-6 text-3xl',
  xl: 'size-6 text-3xl',
};

// PersonalStatus → ring class applied to the avatar circle (None/_0 → no ring)
const STATUS_RING_CLASSES: Partial<Record<PersonalStatus, string>> = {
  [PersonalStatus._1]: 'ring-2 ring-green-500', // Available
  [PersonalStatus._2]: 'ring-2 ring-amber-400', // Interested
  [PersonalStatus._3]: 'ring-2 ring-red-500', // NotAvailable
};

// Profile type → fallback background + text color
const TYPE_FALLBACK_CLASSES: Record<AppProfileType, string> = {
  [AppProfileType.None]: 'bg-neutral-300 text-white',
  [AppProfileType.Player]: 'bg-neutral-300 text-white',
  [AppProfileType.Staff]: 'bg-neutral-300 text-black',
  [AppProfileType.Coach]: 'bg-neutral-300 text-black',
  [AppProfileType.Manager]: 'bg-neutral-300 text-black',
  [AppProfileType.Club]: 'bg-neutral-300 text-black',
};

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

@Component({
  selector: 'mc-avatar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgpAvatar, NgpAvatarImage, NgpAvatarFallback, McIcon],
  host: {
    class: 'relative inline-flex shrink-0',
    '[class]': 'sizeClass()',
  },
  template: `
    <span ngpAvatar class="inline-flex h-full w-full items-center justify-center overflow-hidden rounded-full" [class]="innerClass()">
      @if (resolvedUrl()) {
        <img ngpAvatarImage [src]="resolvedUrl()" [alt]="name()" class="h-full w-full object-cover" />
      }
      <span ngpAvatarFallback class="font-semibold select-none" [class]="initialsTextClass()">
        {{ initials() }}
      </span>
    </span>

    @if (verified()) {
      <span class="absolute right-0 bottom-0 flex items-center justify-center rounded-full bg-white text-blue-500" [class]="badgeSizeClass()" aria-label="Perfil verificado">
        <mc-icon name="rosette-discount-check-filled" />
      </span>
    }
  `,
})
export class McAvatar {
  /**
   * Raw picture path/filename from the API (e.g. `profiles/abc123.jpg`).
   * McAvatar prepends `environment.storageBaseUrl` automatically.
   * Pass undefined/null/empty to show the initials fallback.
   */
  readonly picture = input<string | undefined | null>();

  /** Display name used for initials fallback and img alt text. */
  readonly name = input.required<string>();

  /** Profile type, used to determine the fallback background color. */
  readonly type = input<AppProfileType | undefined>(undefined);

  /** Show a verified badge on the avatar. */
  readonly verified = input(false);

  /**
   * Player/staff availability status. Shows a coloured ring around the avatar:
   * green (Available), amber (Interested), red (NotAvailable).
   * Omit or pass None/_0 to show no ring.
   */
  readonly status = input<PersonalStatus | undefined>(undefined);

  /** Predefined size: sm (32px), md (48px), lg (96px), xl (312px). Defaults to md. */
  readonly size = input<AvatarSize>('md');

  protected readonly resolvedUrl = computed(() => {
    const pic = this.picture();
    return pic ? `${environment.storageBaseUrl}/${pic}` : undefined;
  });

  protected readonly sizeClass = computed(() => SIZE_CLASSES[this.size()]);
  protected readonly initialsTextClass = computed(() => INITIALS_TEXT_CLASSES[this.size()]);
  protected readonly badgeSizeClass = computed(() => BADGE_SIZE_CLASSES[this.size()]);
  protected readonly fallbackBgClass = computed(() => TYPE_FALLBACK_CLASSES[this.type() ?? AppProfileType.None]);

  protected readonly statusRingClass = computed(() => {
    // const s = this.status();
    // if (s == null || s === PersonalStatus._0) return 'ring-2 ring-white';
    // return STATUS_RING_CLASSES[s] ?? 'ring-2 ring-white';
    return 'ring-2 ring-white'; // Temporalmente desactivamos los anillos de estado personal para todos los usuarios
  });

  /** Combined class for the inner avatar circle (bg + optional status ring). */
  protected readonly innerClass = computed(() => `${this.fallbackBgClass()} ${this.statusRingClass()}`.trim());

  protected readonly initials = computed(() => getInitials(this.name()));
}
